import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  updateSharedContact,
  updateSharedContactUserPortal,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import GeneralDialog from '../../../shared/general-dialog';
import AccordionFormSection from './contact-accordian-section';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Page, SharedContactModel } from '../../../../types';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  UserViewRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface EditContactProps {
  open: boolean;
  handleClose: () => void;
  contactList: SharedContactModel[];
  selectedContact: SharedContactModel;
  setSelectedContact: Dispatch<SetStateAction<SharedContactModel | null>>;
  primaryDomain: string;
  page: number;
  pageSize: number;
  searchTerm: string;
  userView?: boolean;
}

export default function EditContactDialog({
  open,
  handleClose,
  contactList,
  selectedContact,
  setSelectedContact,
  primaryDomain,
  page,
  pageSize,
  searchTerm,
  userView,
}: EditContactProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { changeLogRoles, userViewRoles, directoryRoles } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: SharedContactModel) =>
      updateSharedContact(primaryDomain, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Shared contact updated successfully!',
      });

      let newList = contactList?.filter((contact) => contact.id != data.id);
      newList = [...newList, data];
      const pageObject: Page<SharedContactModel[]> | undefined =
        queryClient.getQueryData([
          UserKeys.SharedContacts,
          page,
          pageSize,
          searchTerm,
        ]);
      queryClient.setQueryData(
        [UserKeys.SharedContacts, page, pageSize, searchTerm],
        {
          ...pageObject,
          results: newList,
        }
      );
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedContacts, page, pageSize, ''],
      });
      reset();
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      handleClose();
      setSelectedContact(null);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this shared contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.shared_contacts.update_shared_contact, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.sharedContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: (data: SharedContactModel) =>
        updateSharedContactUserPortal(primaryDomain, data),
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Shared contact updated successfully!',
        });

        let newList = contactList?.filter((contact) => contact.id != data.id);
        newList = [...newList, data];
        const pageObject: Page<SharedContactModel[]> | undefined =
          queryClient.getQueryData([
            UserKeys.SharedContacts,
            page,
            pageSize,
            searchTerm,
          ]);
        queryClient.setQueryData(
          [UserKeys.SharedContacts, page, pageSize, searchTerm],
          {
            ...pageObject,
            results: newList,
          }
        );
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedContacts, page, pageSize, ''],
        });
        reset();
        handleClose();
        setSelectedContact(null);
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue updating this shared contact. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.shared_contacts.update_shared_contact, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.sharedContacts,
        });
      },
    });

  function mutateData(data: Partial<SharedContactModel>) {
    const formContact: SharedContactModel = {
      ...selectedContact,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      birthday: data.birthday || '',
      nickName: data.nickName || '',
      name: `${data.firstName} ${data.lastName}`,
      emails: data.emails ? data.emails.map((email) => email) || [] : [],
      addresses: data.addresses
        ? data.addresses.filter((address) => {
            if (address.addrVal?.addressString != '') {
              return address;
            }
          }) || []
        : [],
      orgs: data.orgs
        ? data.orgs.map((org) => {
            delete org.orgVal?.orgString;
            return org;
          }) || []
        : [],
      phoneNumbers: data.phoneNumbers
        ? data.phoneNumbers.map((phoneNumber) => phoneNumber) || []
        : [],
      webSites: data.webSites
        ? data.webSites.map((webSite) => webSite) || []
        : [],
      im: data.im ? data.im : [],
      relations: data.relations ? data.relations : [],
      events: data.events ? data.events.map((event) => event) || [] : [],
      updated: 0,
    };
    delete formContact.userDomain;
    delete formContact.userName;
    delete formContact.prefix;
    return formContact;
  }

  const onSubmit: SubmitHandler<Partial<SharedContactModel>> = (data) => {
    if (
      (!userView &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSharedContacts.value
        )) ||
      (userView &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserViewRoles.EditSharedContacts.value
        ))
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const formPerson = mutateData(data);
      if (userView) {
        userPortalMutate(formPerson);
      } else {
        mutate(formPerson);
      }
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    formState,
    setValue,
    watch,
    getValues,
  } = useForm<Partial<SharedContactModel>>({
    values: {
      ...selectedContact,
      firstName: selectedContact.firstName ? selectedContact.firstName : '',
      lastName: selectedContact.lastName ? selectedContact.lastName : '',
      nickName: selectedContact.nickName ? selectedContact.nickName : '',
      birthday: selectedContact.birthday ? selectedContact.birthday : '',
      events: selectedContact.events ? selectedContact.events : [],
      addresses: selectedContact.addresses ? selectedContact.addresses : [],
      emails: selectedContact.emails ? selectedContact.emails : [],
      orgs: selectedContact.orgs ? selectedContact.orgs : [],
      phoneNumbers: selectedContact.phoneNumbers
        ? selectedContact.phoneNumbers
        : [],
      im: selectedContact.im ? selectedContact.im : [],
      relations: selectedContact.relations ? selectedContact.relations : [],
      webSites: selectedContact.webSites ? selectedContact.webSites : [],
    },
  });

  const { isDirty } = formState;

  const isDisabled = userView
    ? !isAbleToSeeUIElement(
        userViewRoles,
        UserViewRoles.EditSharedContacts.value
      )
    : !isAbleToSeeUIElement(
        directoryRoles,
        DirectoryRoles.EditSharedContacts.value
      );

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
        setSelectedContact(null);
      }}
      title={`Edit Contact`}
      maxWidth="md"
      dialogContent={
        <Stack
          sx={{ p: 2, justifyContent: 'center', mx: 'auto' }}
          component="form"
        >
          <Stack
            flexDirection="row"
            justifyContent="center"
            gap={6}
            sx={{ p: 2 }}
          >
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  autoComplete="off"
                  label="First Name"
                  sx={{ width: 300 }}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  autoComplete="off"
                  label="Last Name"
                  sx={{ minWidth: 300 }}
                />
              )}
            />
          </Stack>
          <Stack
            flexDirection="row"
            justifyContent="center"
            gap={6}
            sx={{ p: 2 }}
          >
            <Controller
              name="nickName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  autoComplete="off"
                  label="NickName"
                  sx={{ minWidth: 300 }}
                />
              )}
            />
            <Controller
              name={'birthday'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  label="Birthday"
                  sx={{ width: 300 }}
                  disabled={isDisabled}
                  disableFuture
                  value={value ? dayjs(value) : null}
                  onChange={(e) => {
                    if (e) {
                      onChange(dayjs(e).format('YYYY-MM-DD'));
                    }
                  }}
                />
              )}
            />
          </Stack>
          <Stack width={600} sx={{ mx: 'auto', width: 700 }}>
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="emails"
              title="Emails"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[
                {
                  val: 'Primary',
                  label: 'Primary',
                },
                {
                  val: 'home',
                  label: 'Home',
                },
                { val: 'work', label: 'Work' },
                {
                  val: 'other',
                  label: 'Other',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="addresses"
              title="Addresses"
              selectPropertyOne="rel"
              inputProperty="addressString"
              menuItemsOne={[
                {
                  val: 'home',
                  label: 'Home',
                },
                { val: 'work', label: 'Work' },
                {
                  val: 'other',
                  label: 'Other',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="orgs"
              title="Organizations"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[
                { val: 'work', label: 'Work' },
                {
                  val: 'other',
                  label: 'Other',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="phoneNumbers"
              title="Phones"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[
                {
                  val: 'home',
                  label: 'Home',
                },
                { val: 'work', label: 'Work' },
                {
                  val: 'other',
                  label: 'Other',
                },
                {
                  val: 'mobile',
                  label: 'Mobile',
                },
                {
                  val: 'home_fax',
                  label: 'Home Fax',
                },
                {
                  val: 'work_fax',
                  label: 'Work Fax',
                },
                {
                  val: 'pager',
                  label: 'Pager',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="relations"
              title="Relations"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[
                { val: 'SPOUSE', label: 'Spouse' },
                { val: 'CHILD', label: 'Child' },
                { val: 'MOTHER', label: 'Mother' },
                { val: 'FATHER', label: 'Father' },
                { val: 'PARENT', label: 'Parent' },
                { val: 'BROTHER', label: 'Brother' },
                { val: 'SISTER', label: 'Sister' },
                { val: 'FRIEND', label: 'Friend' },
                { val: 'RELATIVE', label: 'Relative' },
                { val: 'MANAGER', label: 'Manager' },
                { val: 'ASSISTANT', label: 'Assistant' },
                { val: 'REFERRED_BY', label: 'Referred By' },
                { val: 'PARTNER', label: 'Partner' },
                { val: 'DOMESTIC_PARTNER', label: 'Domestic Partner' },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="im"
              title="Instant Messaging"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[
                {
                  val: 'GOOGLE_TALK',
                  label: 'Google Talk',
                },
                { val: 'AIM', label: 'AIM' },
                {
                  val: 'YAHOO',
                  label: 'Yahoo',
                },
                {
                  val: 'SKYPE',
                  label: 'Skype',
                },
                { val: 'QQ', label: 'QQ' },
                { val: 'MSN', label: 'MSN' },
                { val: 'ICQ', label: 'ICQ' },
                {
                  val: 'JABBER',
                  label: 'Jabber',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="events"
              title="Events"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[{ val: 'Anniversary', label: 'Anniversary' }]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <AccordionFormSection
              control={control}
              disabled={isDisabled}
              controlName="webSites"
              title="Websites"
              selectPropertyOne="rel"
              inputProperty="val"
              menuItemsOne={[
                { val: 'PROFILE', label: 'Profile' },
                {
                  val: 'BLOG',
                  label: 'Blog',
                },
                { val: 'HOME_PAGE', label: 'Home Page' },
                { val: 'WORK', label: 'Work' },
                { val: 'OTHER', label: 'Other' },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const formContact = mutateData({ ...getValues() });
              mutate(formContact);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: handleSubmit(onSubmit),
        loading: isPending || userPortalPending,
        disabled: !isDirty || isPending || userPortalPending,
      }}
    />
  );
}
